import { A } from "components/A";
import { BrandLinkButton } from "components/BrandButton";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { DerpibooruIcon } from "components/DerpibooruIcon";
import { ExploreTags } from "components/ExploreTags";
import { PostGrid } from "components/PostGrid";
import { SubscribeStarIcon } from "components/SubscribeStarIcon";
import { groupedPosts, tags } from "data/posts";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { MdAdd } from "react-icons/md";
import { SiBluesky, SiDiscord, SiMega, SiPatreon, SiX } from "react-icons/si";
import { useIntersectionObserver } from "usehooks-ts";
import { usePrevious } from "../hooks";

// export const getStaticProps: GetStaticProps = async () => {
//   const format = "yyyy-MM-dd";
//   const days = 2;
//   const end = new Date();
//   const start = addDays(new Date(), -days - 1);
//   const params = {
//     site_id: "irisarco.art",
//     period: "custom",
//     date: `${lightFormat(start, format)},${lightFormat(end, format)}`,
//     property: "event:page",
//     metrics: "visitors",
//     filters: "event:page==/images/*/*",
//     limit: "12",
//   };

//   const { results } = await (
//     await fetch(
//       `https://plausible.io/api/v1/stats/breakdown?${new URLSearchParams(
//         params,
//       ).toString()}`,
//       { headers: { Authorization: `Bearer ${process.env.PLAUSIBLE_KEY}` } },
//     )
//   ).json();

//   const ids: number[] = [
//     ...new Set<number>(
//       results.map((r: { page: string }) => r.page.split("/").at(2)).map(Number),
//     ).values(),
//   ];

//   const popular = ids.map((id) => posts.find((p) => p.id === id));

//   return {
//     props: {
//       popular,
//     },
//     revalidate: 3600, // 1 hour
//   };
// };

const Home = () => {
  const { ref, isIntersecting } = useIntersectionObserver({
    rootMargin: "800px",
  });
  const router = useRouter();
  const slice = (router.isReady && Number(router.query.slice)) || 1;
  const wasIntersecting = usePrevious(isIntersecting);

  useEffect(() => {
    if (isIntersecting && !wasIntersecting && slice < groupedPosts.length) {
      router.replace({ pathname: "/", query: { slice: slice + 1 } }, "/", {
        shallow: true,
      });
    }
  }, [isIntersecting, router, slice, wasIntersecting]);

  return (
    <div className="-mt-14 mb-10">
      <Head>
        <style>{`html { scrollbar-gutter: stable; }`}</style>
      </Head>

      {/* 12 for navbar and 2 for layout padding */}
      <div>
        <div className="relative h-60 overflow-hidden md:h-80 xl:h-[500px]">
          <Image
            src="/banner.webp"
            alt="banner"
            className="object-cover"
            fill
            priority
          />
        </div>
        <div
          className="absolute top-0 right-0 left-0 -z-10 h-[1000px] opacity-20 blur-3xl dark:opacity-20"
          style={{
            backgroundColor: "rgba(168, 164, 208, 1)",
            backgroundImage:
              "linear-gradient(90deg, rgba(187, 110, 206, 1) 0%, rgba(214, 85, 175, 1) 50%, rgba(64, 141, 212, 1) 100%)",
          }}
        />
      </div>

      <Card
        rounded={false}
        className="relative -mt-2 mb-3 flex w-full flex-col px-2 py-5 backdrop-blur-sm md:px-5"
      >
        <div className="container leading-snug">
          <div>
            <h1 className="mx-auto mb-1 text-3xl font-medium sm:text-5xl md:text-6xl lg:text-7xl">
              IRISARCO&apos;S ART SITE
            </h1>
            <p className="text-primary-12/95 mb-2 text-xl md:text-3xl dark:text-slate-300">
              Hello, I am irisarco and I like to draw pony girls
            </p>
          </div>
          <div className="max-w-3xl space-y-1">
            <p>
              Here you can find all my pics. Images tagged with “AltV” make use
              of my{" "}
              <A href="https://altv.irisarco.art" target="_blank">
                AltV project
              </A>
              .
            </p>
            <p>
              I post my art on{" "}
              <A
                href="https://twitter.com/irisarconsfw"
                target="_blank"
                rel="noreferrer"
              >
                Twitter
              </A>
              , you can follow me there. If you&apos;d like to see sketches,
              WIPs, share ideas, feedback or maybe just chat then my{" "}
              <A
                href="https://discord.gg/NsZPUMbvMN"
                target="_blank"
                rel="noreferrer"
              >
                Discord server
              </A>{" "}
              is the best place for that.
            </p>
            <p>
              You can{" "}
              <A
                href="https://subscribestar.adult/irisarco"
                target="_blank"
                rel="noreferrer"
              >
                support me
              </A>{" "}
              if you like what I do :)
            </p>
            <p className="font-medium text-neutral-800 dark:text-slate-300">
              All characters are over 18 years old.
            </p>
          </div>
        </div>
      </Card>

      <div className="container">
        <div className="mb-1 text-lg">Find me on:</div>
        <div className="mb-4 flex flex-wrap gap-2 min-[390px]:after:grow-3">
          <BrandLinkButton
            href="https://subscribestar.adult/irisarco"
            className="flex-1 justify-center bg-[#FF4081] text-white"
            icon={<SubscribeStarIcon />}
          >
            SubscribeStar
          </BrandLinkButton>
          <BrandLinkButton
            href="https://patreon.com/irisarco"
            className="flex-1 justify-center bg-[#FF424D] text-white"
            icon={<SiPatreon />}
          >
            Patreon
          </BrandLinkButton>
          <BrandLinkButton
            href="https://twitter.com/irisarconsfw"
            className="flex-1 justify-center bg-[#1DA1F2] text-white"
            icon={<SiX />}
          >
            Twitter
          </BrandLinkButton>
          <BrandLinkButton
            href="https://bsky.app/profile/irisarco.art"
            className="flex-1 justify-center bg-[#0085ff] text-white"
            icon={<SiBluesky />}
          >
            Bluesky
          </BrandLinkButton>
          <BrandLinkButton
            href="https://discord.gg/NsZPUMbvMN"
            className="flex-1 justify-center bg-[#5865F2] text-white"
            icon={<SiDiscord />}
          >
            Discord
          </BrandLinkButton>
          <BrandLinkButton
            href="https://derpibooru.org/profiles/irisarco"
            className="flex-1 justify-center bg-[#3D92D0] text-white"
            icon={<DerpibooruIcon />}
          >
            Derpibooru
          </BrandLinkButton>
          <BrandLinkButton
            href="https://mega.nz/folder/cfAQhILQ#94TzcWGWK0OQMsrSBD38Ng"
            className="flex-1 justify-center bg-[#d90007] text-white"
            icon={<SiMega />}
          >
            Mega
          </BrandLinkButton>
        </div>

        <ExploreTags tags={tags} />

        {process.env.NODE_ENV === "development" && (
          <div className="mb-2">
            <Button as={Link} href="/images/new" transparent={false}>
              <MdAdd />
              Add new post
            </Button>
          </div>
        )}

        {/* <div className="mb-2">
          <Card className="mb-2 p-2 text-center font-medium text-neutral-800 dark:text-slate-300">
            Popular images
          </Card>
          {popular && <PostGrid posts={popular} limited />}
        </div> */}

        <div className="flex flex-col gap-2">
          {groupedPosts.slice(0, slice).map(([year, posts]) => {
            return (
              <React.Fragment key={year}>
                <div className="w-full">
                  <Card className="p-2 text-center font-medium text-neutral-800 dark:text-slate-300">
                    {year}
                  </Card>
                </div>
                <PostGrid posts={posts} />
              </React.Fragment>
            );
          })}
          <div ref={ref} className="w-full">
            {slice === groupedPosts.length && (
              <Card className="p-2 text-center">
                Wow you&apos;ve got really far, this is the end :)
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
